import type {
  AttendanceDayState,
  AttendanceState,
} from "../../../shared/api/parent";
import type { Lang } from "./copy";

/**
 * C6's window, as a calendar rather than as a list of dates.
 *
 * 🔴 WHY A GRID IS THE HONEST SHAPE AND A TABLE WAS NOT. `parent.service.ts`
 * chose 28 days for one stated reason: *"Long enough for a pattern (**every
 * Monday**) to be visible and short enough that a parent is not reading last
 * term's absences as news."* A one-column table of three dates cannot show a
 * pattern — you have to hold three weekday names in your head and compare them.
 * 28 = 4 × 7 exactly, so laid out seven to a row **every column is the same
 * weekday**, and "every Monday" is a vertical line a parent sees without
 * counting anything.
 *
 * 🔴 AND IT STILL SAYS ONLY WHAT THE DATABASE KNOWS. `Absence` holds absences; a
 * day nobody took the register on writes no row, which is identical in the data
 * to a day the whole class came in. So a tile is **absent** or **no absence
 * recorded** — never "present". Naming the second state that way in the legend
 * is the whole difference between an honest calendar and a reassuring one, and
 * it is the same reason the screen refuses to print a percentage.
 *
 * 🔴 PURE, AND TESTED, BECAUSE THIS REPO HAS NO DOM RENDERER (OD-60). A date
 * loop written inside JSX is a date loop no test can reach — and this one decides
 * which square on a parent's screen is marked against their child.
 */

export interface CalendarDay {
  /** `YYYY-MM-DD` — the same key `Absence.day` is stored at. */
  key: string;
  /**
   * 🔴 THE SERVER'S ANSWER, NOT ONE DERIVED HERE. `ParentService.attendance`
   * folds absences, registers and the school's holidays into one state per day
   * and owns the precedence between them. This module places the squares; it
   * does not decide what they say.
   */
  state: AttendanceState;
  /** Set only on a `HOLIDAY` day — "Onam". */
  holidayName: string | null;
  /** 1–31, the numeral drawn in the tile. */
  dayOfMonth: number;
  /**
   * "Aug", on the first tile of a month and on the first tile overall.
   *
   * ⚠️ WITHOUT IT A 28-DAY WINDOW SPANS TWO MONTHS AND SAYS SO NOWHERE — "8"
   * next to "25" is unreadable when one is August and the other July.
   */
  monthLabel: string | null;
  absent: boolean;
  /** The full date, for the tooltip and the accessible name. */
  label: string;
}

export interface AttendanceCalendar {
  days: CalendarDay[];
  /**
   * 🔴 ABSENCES THE GRID CANNOT SHOW, AND THE REASON THIS FIELD EXISTS.
   *
   * The server windows on `day >= now − 28×86400000`, which is a *timestamp*, so
   * it can return an absence on the twenty-ninth calendar day back. The grid is
   * 28 whole days ending today. Without this the count tile ("3 days absent")
   * and the grid (two marks) would disagree in front of a parent, and the grid
   * would be the one that looked broken.
   */
  earlier: CalendarDay[];
  /** Seven narrow weekday letters, aligned to the columns. Decorative. */
  weekdays: string[];
}

/** `YYYY-MM-DD` from a date's LOCAL parts — never `toISOString()`. */
export function dayKey(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * The one locale the product ships in.
 *
 * ⚠️ A CONSTANT RATHER THAN A LOOKUP, because there is one language. It stays
 * named so that the places a locale matters are still greppable if a second
 * one is ever added back.
 */
const LOCALE = "en-IN";

/**
 * A date moved by whole calendar days.
 *
 * ⚠️ `setDate` ARITHMETIC, NOT EPOCH MILLISECONDS — the same rule the window
 * loop below is written to, and for the same reason: subtracting `n * 86400000`
 * lands an hour out across any DST boundary and silently repeats or drops a
 * calendar day for a parent reading this from outside India.
 */
export function shiftDays(from: Date, days: number): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/**
 * `YYYY-MM-DD` as a date a human reads.
 *
 * ⚠️ `T00:00:00Z` IS APPENDED AND THE FORMATTER IS PINNED TO UTC. `new
 * Date('2026-08-11')` parses as UTC midnight and then prints in the viewer's
 * zone — which is the previous day for anyone west of Greenwich, and is a class
 * of bug this codebase has already paid for once in `Absence.day`.
 */
export function formatDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * The same date without the weekday — "8 Aug".
 *
 * ⚠️ FOR A CHIP, NOT FOR A ROW. Two of these join into one span label ("29 Jul –
 * 11 Aug") on a control narrow enough that "Tue, 29 Jul – Mon, 11 Aug" would
 * wrap onto the card's heading. Everywhere a single date is *read* rather than
 * bounded, `formatDay` still wins: the weekday is the part a parent recognises.
 */
export function formatDayShort(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(LOCALE, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * The window, oldest first, ending today.
 *
 * ⚠️ STEPPED WITH `setDate`, NOT BY SUBTRACTING MILLISECONDS. Arithmetic on
 * epoch ms lands an hour out across any DST boundary and silently drops or
 * repeats a calendar day. India has no DST, but a parent opening this from
 * abroad — which is a large part of who this product's parents are — would get a
 * grid with two Tuesdays in it.
 */
export function attendanceCalendar(input: {
  windowDays: number;
  absences: readonly string[];
  /**
   * The server's per-day states.
   *
   * ⚠️ OPTIONAL, AND ITS ABSENCE IS A REAL CASE RATHER THAN A DEFENSIVE ONE. A
   * server that predates `RegisterDay` sends no `days`, and the honest fallback
   * is the calendar this screen already drew: absences marked, every other day
   * unknown. It must never fall back to `PRESENT`, which is the one state that
   * needs a record behind it.
   */
  states?: readonly AttendanceDayState[];
  lang: Lang;
  /** Injected so the tests are not a clock. */
  today?: Date;
}): AttendanceCalendar {
  const { windowDays, absences } = input;
  const today = input.today ?? new Date();
  const absent = new Set(absences);
  const byDay = new Map((input.states ?? []).map((s) => [s.day, s]));
  const locale = LOCALE;

  const days: CalendarDay[] = [];
  let lastMonth: number | null = null;

  for (let i = windowDays - 1; i >= 0; i--) {
    const d = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - i,
    );
    const key = dayKey(d);
    const month = d.getMonth();
    const sent = byDay.get(key);
    days.push({
      key,
      /*
       * ⚠️ THE ABSENCE LIST STILL WINS WHERE THE SERVER SENT NO STATE. The
       * browser's "today" and the server's can differ by one across midnight, so
       * the grid can reach a day the server did not send — and an absence known
       * from the other field must not vanish because of a clock.
       */
      state: sent?.state ?? (absent.has(key) ? "ABSENT" : "NO_REGISTER"),
      holidayName: sent?.holidayName ?? null,
      dayOfMonth: d.getDate(),
      monthLabel:
        month === lastMonth
          ? null
          : d.toLocaleDateString(locale, { month: "short" }),
      absent: absent.has(key),
      label: formatDay(key),
    });
    lastMonth = month;
  }

  const inGrid = new Set(days.map((d) => d.key));
  const earlier = absences
    .filter((day) => !inGrid.has(day))
    /* Newest first, matching the order the server sends absences in. */
    .sort((a, b) => (a < b ? 1 : -1))
    .map((day) => ({
      key: day,
      state: "ABSENT" as const,
      holidayName: null,
      dayOfMonth: Number(day.slice(8, 10)),
      monthLabel: null,
      absent: true,
      label: formatDay(day),
    }));

  return { days, earlier, weekdays: weekdaysOf(days, locale) };
}

/**
 * The seven column headings, taken from the grid's own first week.
 *
 * 🔴 DERIVED, NEVER A LITERAL ARRAY. A hardcoded `['M','T','W',…]` would be
 * right only for a window that happens to start on a Monday, and wrong every
 * other day of the year — the columns are whatever weekdays the window landed
 * on, and the headings have to be read off the same days the tiles are.
 */
function weekdaysOf(days: readonly CalendarDay[], locale: string): string[] {
  return days.slice(0, 7).map((d) =>
    new Date(`${d.key}T00:00:00Z`).toLocaleDateString(locale, {
      weekday: "narrow",
      timeZone: "UTC",
    }),
  );
}

/* ──────────────────────────────────────────────── one calendar month ── */

/**
 * A day in a month grid, with the two things a rolling window never had to say.
 *
 * 🔴 A MONTH CONTAINS DAYS NOTHING CAN BE KNOWN ABOUT, AND A ROLLING WINDOW DOES
 * NOT. `attendanceCalendar` ends today, so every square in it is a day that has
 * happened and that the response covered. Draw August on the 23rd and eight
 * squares are the FUTURE; walk back to a month before the window and the whole
 * grid is days the server was never asked about. Both would otherwise fall
 * through to `NO_REGISTER` — "no register taken" — which is a statement about a
 * school's record-keeping, made here about days that have not happened yet.
 *
 * So they are their own flags, they are drawn differently, and `countable`
 * excludes them from every figure on the screen.
 */
export interface MonthDay extends CalendarDay {
  /** After today. Nothing has happened yet, and nothing is claimed. */
  future: boolean;
  /** Older than the oldest day the response covered. Unknown, and says so. */
  outsideWindow: boolean;
  /** Today itself, which the grid rings. */
  isToday: boolean;
}

export interface AttendanceMonth {
  year: number;
  /** 0–11, as `Date.getMonth()` gives it. */
  month: number;
  /** "August 2026" — the heading over the grid. */
  label: string;
  /** Seven narrow letters, Monday first. Decorative. */
  weekdays: string[];
  /**
   * Whole weeks, Monday first, with `null` for the leading and trailing padding.
   *
   * ⚠️ PADDED WITH NULLS RATHER THAN WITH THE NEIGHBOURING MONTHS' DAYS. A
   * greyed-out 31 July sitting in August's first row is a square a parent can
   * tap, in a grid whose heading says August — and this screen's squares open a
   * day's detail. An empty cell cannot be misread.
   */
  cells: (MonthDay | null)[];
  /** Every day of this month, in order, padding excluded. */
  days: MonthDay[];
  /**
   * The days this month that a figure may be computed from — neither in the
   * future nor outside the window. The screen's ring, its counts and its
   * percentages all divide by this and nothing else.
   */
  countable: MonthDay[];
}

/** Monday-first column index for a `Date`: Mon → 0 … Sun → 6. */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/**
 * One calendar month of attendance — C6's grid once the arrows became months.
 *
 * 🔴 MONDAY FIRST, WHICH IS A SCHOOL WEEK RATHER THAN A LOCALE DEFAULT. The
 * question this grid is read for is *"is it always a Monday?"*, and the pattern
 * only shows as a vertical line if the week starts where the school week does.
 *
 * 🔴 THE STATE IS STILL THE SERVER'S. `attendanceCalendar`'s rule holds here
 * unchanged: three tables decide what a day means and the precedence between
 * them is a product decision. This module places squares in a month; it does not
 * decide what any of them say.
 */
export function attendanceMonth(input: {
  year: number;
  /** 0–11. */
  month: number;
  /** The server's per-day states, any length — days outside this month are ignored. */
  states?: readonly AttendanceDayState[];
  absences: readonly string[];
  /**
   * `YYYY-MM-DD` — the oldest day the response actually covered.
   *
   * ⚠️ WITHOUT IT EVERY DAY IS TREATED AS COVERED, which is the honest fallback
   * for a server that sends no `days`: the grid then marks absences and says
   * "no register" for the rest, exactly as it did before months existed.
   */
  windowStart?: string;
  lang: Lang;
  /** Injected so the tests are not a clock. */
  today?: Date;
}): AttendanceMonth {
  const today = input.today ?? new Date();
  const todayKey = dayKey(today);
  const absent = new Set(input.absences);
  const byDay = new Map((input.states ?? []).map((s) => [s.day, s]));

  const first = new Date(input.year, input.month, 1);
  /* Day 0 of the next month is the last day of this one — no table of lengths,
     and February answers for itself in a leap year. */
  const length = new Date(input.year, input.month + 1, 0).getDate();

  const days: MonthDay[] = [];
  for (let n = 1; n <= length; n++) {
    const d = new Date(input.year, input.month, n);
    const key = dayKey(d);
    const sent = byDay.get(key);
    days.push({
      key,
      state: sent?.state ?? (absent.has(key) ? "ABSENT" : "NO_REGISTER"),
      holidayName: sent?.holidayName ?? null,
      dayOfMonth: n,
      /* The month is in the heading over the grid, so a per-tile label would be
         the same word thirty times. */
      monthLabel: null,
      absent: absent.has(key),
      label: formatDay(key),
      future: key > todayKey,
      outsideWindow:
        input.windowStart !== undefined && key < input.windowStart,
      isToday: key === todayKey,
    });
  }

  const lead = mondayIndex(first);
  const cells: (MonthDay | null)[] = [
    ...(Array.from({ length: lead }, () => null) as null[]),
    ...days,
  ];
  /* Padded to whole weeks so the seven columns stay seven columns — the reason
     the grid can be read down at all. */
  while (cells.length % 7 !== 0) cells.push(null);

  return {
    year: input.year,
    month: input.month,
    label: first.toLocaleDateString(LOCALE, { month: "long", year: "numeric" }),
    weekdays: mondayFirstWeekdays(),
    cells,
    days,
    countable: days.filter((d) => !d.future && !d.outsideWindow),
  };
}

/**
 * The seven column headings for a Monday-first grid.
 *
 * 🔴 DERIVED FROM REAL DATES, NOT A LITERAL ARRAY — the same rule `weekdaysOf`
 * is written to. 5 January 1970 was a Monday, so seven days from it are the
 * seven weekday names in the order this grid draws them, and the locale gets to
 * name them rather than this file.
 */
function mondayFirstWeekdays(): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    new Date(Date.UTC(1970, 0, 5 + i)).toLocaleDateString(LOCALE, {
      weekday: "narrow",
      timeZone: "UTC",
    }),
  );
}

/**
 * The months a parent may open, newest first — every month the window touches.
 *
 * ⚠️ IT IS DERIVED FROM THE WINDOW RATHER THAN FROM A COUNT. The server states
 * how far back its answer reaches; offering a month past that would be offering
 * a grid of squares nobody can say anything about.
 */
export function monthsInWindow(input: {
  windowStart: string;
  today?: Date;
}): { year: number; month: number; label: string }[] {
  const today = input.today ?? new Date();
  const startYear = Number(input.windowStart.slice(0, 4));
  const startMonth = Number(input.windowStart.slice(5, 7)) - 1;

  const out: { year: number; month: number; label: string }[] = [];
  const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  const floor = new Date(startYear, startMonth, 1);
  while (cursor.getTime() >= floor.getTime()) {
    out.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      label: cursor.toLocaleDateString(LOCALE, {
        month: "long",
        year: "numeric",
      }),
    });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return out;
}
